import * as Sentry from "@sentry/react";
import type {
  AddTutorToClassRequest,
  CollegeAdvisingSignupRequest,
  CreateClassSessionRequest,
  CreateInvoiceRequest,
  CreateInvoiceResponse,
  CreateOneTimeBookingRequest,
  CreateOneTimeBookingResponse,
  CreatePaypalAPIResponse,
  CreateStripeCheckoutSessionRequest,
  CreateStripeResponse,
  EmailRequest,
  FinalizeInvoiceRequest,
  FinalizeInvoiceResponse,
  GroupClassSignupRequest,
  MessageOrProblem,
  PaypalCaptureRequest,
  PaypalCaptureResponse,
  PaypalOrderCreateRequest,
  RemoveTutorFromClassRequest,
  TextRequest,
} from "@shared/backend-api.types.ts";
import { ProblemJsonSchema } from "@shared/backend-api.types.ts";
import type { Event, EventParticipant, Session, Transaction } from "@shared/supabase.types.ts";

import { getAccessToken } from "./auth-utilities";

const SUPABASE_BACKEND_URL = import.meta.env.VITE_SUPABASE_BACKEND_URL;

// Custom response types not defined in backend-api.types.ts
export type GroupClassSignupResponse = {
  message: string;
  transaction: Transaction;
  eventIds: number[];
};

export type CreateClassSessionResponse = {
  session: Session;
  event: Event;
};

export type AddTutorToClassResponse = {
  message: string;
  transaction: Transaction;
  eventParticipant: EventParticipant;
};

export type RemoveTutorFromClassResponse = {
  message: string;
};

// Create a type map for your backend routes
export type ApiRoutes = {
  // Actions
  "/actions/create-one-time-booking": {
    method: "POST";
    body: CreateOneTimeBookingRequest;
    response: CreateOneTimeBookingResponse;
  };
  "/actions/group-class-signup": {
    method: "POST";
    body: GroupClassSignupRequest;
    response: GroupClassSignupResponse;
  };
  "/actions/create-class-session": {
    method: "POST";
    body: CreateClassSessionRequest;
    response: CreateClassSessionResponse;
  };
  "/actions/signup-college-advising": {
    method: "POST";
    body: CollegeAdvisingSignupRequest;
    response: MessageOrProblem;
  };
  "/actions/add-tutor-to-class": {
    method: "POST";
    body: AddTutorToClassRequest;
    response: AddTutorToClassResponse;
  };
  "/actions/remove-tutor-from-class": {
    method: "POST";
    body: RemoveTutorFromClassRequest;
    response: RemoveTutorFromClassResponse;
  };

  // Notifications
  "/notifications/email": {
    method: "POST";
    body: EmailRequest;
    response: MessageOrProblem;
  };
  "/notifications/text": {
    method: "POST";
    body: TextRequest;
    response: MessageOrProblem;
  };
  // "/notifications/payment-reminders": {
  //     method: "POST";
  //     body: never;
  //     response: MessageOrProblem;
  // };
  // "/notifications/event-reminders": {
  //     method: "POST";
  //     body: never;
  //     response: MessageOrProblem;
  // };

  // Payments
  "/payments/paypal/order": {
    method: "POST";
    body: PaypalOrderCreateRequest;
    response: CreatePaypalAPIResponse;
  };
  "/payments/paypal/order/capture": {
    method: "POST";
    body: PaypalCaptureRequest;
    response: PaypalCaptureResponse;
  };
  "/payments/stripe/create-invoice": {
    method: "POST";
    body: CreateInvoiceRequest;
    response: CreateInvoiceResponse;
  };
  "/payments/stripe/finalize-invoice": {
    method: "POST";
    body: FinalizeInvoiceRequest;
    response: FinalizeInvoiceResponse;
  };
  "/payments/stripe/checkout": {
    method: "POST";
    body: CreateStripeCheckoutSessionRequest;
    response: CreateStripeResponse;
  };
};

/**
 * A generic function to call backend API endpoints.
 * It handles authentication and basic error handling.
 *
 * @param path The API route path from ApiRoutes.
 * @param method The HTTP method for the request.
 * @param body The request body, if the endpoint requires one.
 * @returns The JSON response from the backend.
 */
async function callBackend<T extends keyof ApiRoutes>(
  path: T,
  method: ApiRoutes[T]["method"],
  ...args: ApiRoutes[T]["body"] extends never ? [] : [ApiRoutes[T]["body"]]
): Promise<ApiRoutes[T]["response"]> {
  const idToken = await getAccessToken();
  const body = args[0];

  const response = await fetch(`${SUPABASE_BACKEND_URL}/backend-api${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  Sentry.addBreadcrumb({
    message: `API call to ${path} with method ${method}`,
    level: "info",
    data: {
      body,
    },
  });

  if (!response.ok) {
    const errorBody = await response.text();
    console.error(
      `API call to ${path} failed: ${response.status} ${response.statusText}`,
      errorBody,
    );
    Sentry.captureException(new Error(`API call to ${path} failed`), {
      extra: {
        status: response.status,
        statusText: response.statusText,
        body: errorBody,
      },
    });
    try {
      const problem = ProblemJsonSchema.parse(JSON.parse(errorBody));
      throw new Error(problem.detail || problem.title || `API call failed`);
    } catch (e) {
      throw new Error(`API call failed: ${response.status} ${response.statusText} - ${errorBody}`);
    }
  }

  const responseJson = await response.json();
  Sentry.addBreadcrumb({
    message: `API call to ${path} succeeded`,
    level: "info",
    data: {
      response: responseJson,
    },
  });

  return responseJson;
}

export { callBackend };
